import {
  AgentThreadRole,
  isAgentThreadClientContextContent,
  type AgentThreadRecord,
  type ClientViewingContext,
} from "./AgentThreadRecord";

/**
 * The board a chat message's board requests apply to: the board of the
 * board page the message was sent from. A board tool builds or changes this
 * board, and `client_create_board` refuses to create another unless its
 * input says the user asked for a new board.
 */
export interface RequestsApplyTo {
  boardId: string;
}

/** The board page's board as the chat sends it, under `misc_context.board`. */
export interface BoardInMessage {
  id: string;
  title?: string;
  draft: boolean;
  [member: string]: unknown;
}

/**
 * The `misc_context` of a message sent from a board page: the board, under
 * `board`, and under `requests_apply_to` the statement that the message's
 * board requests apply to it. Every sender builds it here, so the board in a
 * message and the board its requests apply to are always the same one.
 */
export function miscContextForBoard(
  board: BoardInMessage,
): Record<string, unknown> {
  return {
    requests_apply_to: { board_id: board.id },
    board,
  };
}

/**
 * The board the thread's newest request applies to, or undefined when that
 * request named none. Walking back from the end of the thread, past the
 * assistant's replies and the tool results, the newest request is the first
 * message that is either a USER message or carries a context block. A ROBOTO
 * message holding only a context block is how the server stores the context
 * of a turn that invokes a skill without a message. A USER message without a
 * context block named no board.
 */
export function requestsApplyToOf(
  thread: AgentThreadRecord | undefined,
): RequestsApplyTo | undefined {
  for (const message of [...(thread?.messages ?? [])].reverse()) {
    const block = message.content.find(isAgentThreadClientContextContent);
    if (block !== undefined) {
      return requestsApplyToIn(block.context);
    }
    if (message.role === AgentThreadRole.USER) {
      return undefined;
    }
  }
  return undefined;
}

function requestsApplyToIn(
  context: ClientViewingContext | undefined,
): RequestsApplyTo | undefined {
  const member = context?.misc_context?.requests_apply_to;
  if (typeof member !== "object" || member === null) {
    return undefined;
  }
  const { board_id: boardId } = member as Record<string, unknown>;
  return typeof boardId === "string" && boardId !== ""
    ? { boardId }
    : undefined;
}
